import { Inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, map, Observable, switchMap, throwError } from 'rxjs';
import { DocumentServiceType, FieldValue, ServiceName, UserType } from '../constants/services.enum';
import { RoutesPath } from '../constants/routes-path';
import { SrStatus } from '../constants/status.enum';
import { ApiEndPoints, ApiRoot } from '../constants/api-endpoints';
import { ApiService } from '../../core/services/api.service';
import { Utils } from '../../Utils/utils';
import { ApplicationRequest } from '../models/application-request.model';
import { AttachmentService } from '../../core/services/attachment.service';
import { DOCUMENT } from '@angular/common';
import {
  AllowedDocumentType,
  DocumentValidationResult,
  FolderResponse,
} from '../models/documents-response.model';
import { EncryptionResponse } from '../models/encryption-response.model';
import { environment } from '../../../environments/environment';
import { PendingApplicationRequest } from '../models/pending-application-request.model';
import { DcMethodValues } from '../constants/dc-method.constants';
import { CorrelationIdService } from '../../core/services/correlation-id.service';

interface TourReminderState {
  lastShownISO: string;
}

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  srStatus = SrStatus;
  private readonly tourReminderStorageKey = 'showTour';
  private readonly tourReminderCooldownMs = 14 * 24 * 60 * 60 * 1000;
  private readonly platinumThemeKey = 'platinumThemeActive';
  private readonly inactiveMemberAccountStatuses = ['Cancelled', 'Inactive', 'InProgress', 'Suspended', 'Hold', 'Rejected'];
  private serviceAccessFlags: Record<string, string> = {};
  private userProfileSubject = new BehaviorSubject<any>(this.getUserProfile());
  userProfile$ = this.userProfileSubject.asObservable();
  private primaryAccountSubject = new BehaviorSubject<any>(this.getPrimaryAccount());
  primaryAccount$ = this.primaryAccountSubject.asObservable();
  private dcUserTypeSubject = new BehaviorSubject<string>(this.getDcUserType());
  dcUserType$ = this.dcUserTypeSubject.asObservable();
  // This method is used to initialize the class instance using toastr, translate, router, api, and attachment service
  constructor(
    private toastr: ToastrService,
    private translate: TranslateService,
    private router: Router,
    private api: ApiService,
    private attachmentService: AttachmentService,
    private correlationIdService: CorrelationIdService,
    @Inject(DOCUMENT) private document: Document
  ) {
    // Apply persisted Platinum theme on load (if set)
    this.applyPlatinumThemeClass(this.isPlatinumThemeActive());
  }

  setPlatinumTheme(active: boolean): void {
    try {
      localStorage.setItem(this.platinumThemeKey, JSON.stringify(Boolean(active)));
    } catch {
      // ignore storage errors
    }
    this.applyPlatinumThemeClass(active);
  }

  isPlatinumThemeActive(): boolean {
    try {
      const stored = localStorage.getItem(this.platinumThemeKey);
      return stored ? JSON.parse(stored) === true : false;
    } catch {
      return false;
    }
  }

  private applyPlatinumThemeClass(active: boolean): void {
    const bodyEl = this.document?.body;
    if (!bodyEl) {
      return;
    }
    const className = 'platinum-theme';
    if (active) {
      bodyEl.classList.add(className);
    } else {
      bodyEl.classList.remove(className);
    }
  }

  // This method is used to get sr number using route
  getSrNumber(route: ActivatedRoute): Observable<string> {
    return route.params.pipe(
      map((params) => {
        if (params['sr_no']) {
          return params['sr_no'];
        } else {
          const stored = sessionStorage.getItem('srNumber');
          return stored ?? '';
        }
      })
    );
  }

  enableNotesOption(status?: string): boolean {
    let enableNotes: boolean = false;
    if (!status) {
      return false;
    }
    if (status == SrStatus.InProgress
      || status == SrStatus.AwaitingClarification
      || status == SrStatus.PendingApproval
      || status == SrStatus.ApprovedAndPaymentAwaited
    ) {
      enableNotes = true;
    }
    return enableNotes;
  }

  canShowSaveAndClose(status?: string): boolean {
    if (!status) {
      return true;
    }

    const normalizedStatus = String(status).trim().toLowerCase();
    const draftStatus = String(SrStatus.Draft).trim().toLowerCase();
    return normalizedStatus === draftStatus || normalizedStatus === 'draft';
  }

  // This method is used to confirm navigation if form dirty using form and is submitted
  confirmNavigationIfFormDirty(
    form: FormGroup,
    isSubmitted: boolean = false
  ): boolean {
    if ((form?.dirty || form?.touched) && !isSubmitted) {
      return confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
    }
    return true;
  }
  // This method is used to set user profile using profile
  setUserProfile(profile: any) {
    localStorage.setItem('userProfile', JSON.stringify(profile));
    this.userProfileSubject.next(profile);
  }

  // This method is used to get user profile
  getUserProfile() {
    let data: any = '';
    if (localStorage.getItem('userProfile') != null) {
      let store: any = localStorage.getItem('userProfile');
      data = JSON.parse(store);
    }
    return data;
  }

  clearUserProfile() {
    localStorage.removeItem('userProfile');
    this.userProfileSubject.next('');
  }

  setServiceAccessFlags(flags: Record<string, string>): void {
    this.serviceAccessFlags = flags || {};
  }

  getServiceAccessFlags(): Record<string, string> {
    return this.serviceAccessFlags || {};
  }

  setDcUserType(userType: string | null | undefined): void {
    const normalizedType = (userType ?? '').toString().trim();
    if (normalizedType) {
      sessionStorage.setItem('DC_User_Type', normalizedType);
    } else {
      sessionStorage.removeItem('DC_User_Type');
    }
    this.dcUserTypeSubject.next(normalizedType);
  }

  getDcUserType(): string {
    return (sessionStorage.getItem('DC_User_Type') ?? '').trim();
  }

  // This method is used to get display status using expiry date and account status
  getMemberDisplayStatus(member: any): string {
    const accountStatus = (member?.['Account Status'] || '').toString().trim();
    if (accountStatus === 'Inactive' || accountStatus === 'Cancelled') {
      return accountStatus;
    }
    if (this.isMemberExpired(member?.['DC Expiry Date'])) {
      return 'Expired';
    }
    return accountStatus;
  }

  isInactiveMemberAccount(member: any): boolean {
    const accountStatus = (member?.['Account Status'] || '').toString().trim();
    return this.inactiveMemberAccountStatuses.includes(accountStatus);
  }

  // This method is used to check if membership is expired
  private isMemberExpired(expiryDate: any): boolean {
    if (!expiryDate) {
      return false;
    }
    const parsedDate = this.parseExpiryDate(expiryDate);
    if (!parsedDate) {
      return false;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    parsedDate.setHours(0, 0, 0, 0);
    return parsedDate < today;
  }

  // This method is used to parse expiry date
  private parseExpiryDate(value: any): Date | null {
    if (value instanceof Date && !isNaN(value.getTime())) {
      return new Date(value);
    }
    const raw = String(value || '').trim();
    if (!raw) {
      return null;
    }
    const parts = raw.split('/');
    if (parts.length === 3) {
      const month = Number(parts[0]);
      const day = Number(parts[1]);
      const year = Number(parts[2]);
      if (
        Number.isFinite(month) &&
        Number.isFinite(day) &&
        Number.isFinite(year) &&
        month >= 1 && month <= 12 &&
        day >= 1 && day <= 31
      ) {
        return new Date(year, month - 1, day);
      }
    }
    const fallback = new Date(raw);
    return isNaN(fallback.getTime()) ? null : fallback;
  }

  isServiceAllowed(service: any, flags?: Record<string, string>): boolean {
    const accountType = this.getPrimaryAccount()?.['Account Type Code'];
    if (accountType === UserType.NON_MEMBER || accountType === UserType.NON_MEMBER_AR) {
      return true;
    }
    const flagKey = service?.accessFlagKey;
    if (!flagKey) {
      return true;
    }
    const accessFlags = flags || this.getServiceAccessFlags();
    return accessFlags?.[flagKey] !== 'N' && accessFlags?.[flagKey] !== '';
  }
  // This method is used to set primary account using user
  setPrimaryAccount(user: any) {
    localStorage.setItem('primaryAccount', JSON.stringify(user));
    this.primaryAccountSubject.next(user);
  }

  // This method is used to get primary account
  getPrimaryAccount() {
    let user: any = '';
    if (localStorage.getItem('primaryAccount') != 'undefined') {
      let store: any = localStorage.getItem('primaryAccount');
      user = JSON.parse(store);
    }
    return user;
  }

  /**
   * Returns true when the logged-in account has at least the provided minimum
   * Advance For Services balance. Defaults to checking for any positive balance.
   */
  hasSufficientAdvanceForServicesBalance(minBalance: number = 1): boolean {
    const primaryAccount = this.getPrimaryAccount();
    const paymentProfile = primaryAccount?.['Organization Payment Profile'];
    if (!paymentProfile) {
      return false;
    }
    const balance = Utils.getAdvancePaymentBalance(paymentProfile);
    return balance >= minBalance;
  }
  // This method is used to get csn account id using type
  getCsnAccountId(type: 'csn' | 'id') {
    let userCSN: string = '';
    if (type == 'csn') {
      //userCSN = "1298";
      if (this.getPrimaryAccount()?.CSN && this.getPrimaryAccount().CSN != '') {
        userCSN = this.getPrimaryAccount()?.CSN;
      }
    } else {
      if (this.getPrimaryAccount()?.Id && this.getPrimaryAccount()?.Id != '') {
        userCSN = this.getPrimaryAccount()?.Id;
      }
    }
    return userCSN;
  }

  // This method is used to set tour reminder using value
  setTourReminder(value: Date | string | null = new Date()) {
    if (value === null) {
      this.clearCookie(this.tourReminderStorageKey);
      localStorage.removeItem(this.tourReminderStorageKey);
      return;
    }

    const reminderDate =
      value instanceof Date
        ? value
        : new Date(typeof value === 'string' ? value : '');

    if (Number.isNaN(reminderDate.getTime())) {
      // Fallback to now if the provided value cannot be parsed as a date
      this.markTourModalShown();
      return;
    }

    this.markTourModalShown(reminderDate);
  }

  // This method is used to get tour reminder
  getTourReminder(): TourReminderState | null {
    return this.parseTourReminder();
  }

  shouldShowTourModal(): boolean {
    const reminder = this.parseTourReminder();
    if (!reminder?.lastShownISO) {
      return true;
    }

    const lastShown = new Date(reminder.lastShownISO);
    if (Number.isNaN(lastShown.getTime())) {
      return true;
    }

    return (Date.now() - lastShown.getTime()) >= this.tourReminderCooldownMs;
  }

  markTourModalShown(referenceDate: Date = new Date()): void {
    const reminder: TourReminderState = { lastShownISO: referenceDate.toISOString() };
    this.writeCookie(
      this.tourReminderStorageKey,
      JSON.stringify(reminder),
      this.tourReminderCooldownMs / 1000
    );
  }

  private parseTourReminder(): TourReminderState | null {
    const rawValue =
      this.readCookie(this.tourReminderStorageKey) ??
      localStorage.getItem(this.tourReminderStorageKey);
    if (!rawValue) {
      return null;
    }

    try {
      const parsed = JSON.parse(rawValue) as TourReminderState | string;
      if (typeof parsed === 'string') {
        const parsedDate = new Date(parsed);
        if (!Number.isNaN(parsedDate.getTime())) {
          return { lastShownISO: parsedDate.toISOString() };
        }
        return parsed === 'true' ? null : { lastShownISO: new Date().toISOString() };
      }
      if (parsed && typeof parsed.lastShownISO === 'string') {
        return parsed;
      }
    } catch {
      const legacyDate = new Date(rawValue);
      if (!Number.isNaN(legacyDate.getTime())) {
        return { lastShownISO: legacyDate.toISOString() };
      }
      if (rawValue === 'true') {
        return null;
      }
      if (rawValue === 'false' || rawValue === '') {
        return { lastShownISO: new Date().toISOString() };
      }
    }

    return null;
  }

  private readCookie(name: string): string | null {
    if (!this.document || typeof this.document.cookie !== 'string') {
      return null;
    }

    const nameEq = `${name}=`;
    const cookieSegments = this.document.cookie.split(';');

    for (const segment of cookieSegments) {
      const trimmed = segment.trim();
      if (trimmed.startsWith(nameEq)) {
        return decodeURIComponent(trimmed.substring(nameEq.length));
      }
    }

    return null;
  }

  private writeCookie(name: string, value: string, maxAgeSeconds: number): void {
    if (!this.document) {
      return;
    }

    const directives = [
      `${name}=${encodeURIComponent(value)}`,
      'Path=/',
      `Max-Age=${Math.max(Math.floor(maxAgeSeconds), 0)}`,
      'SameSite=Lax',
    ];

    if (typeof location !== 'undefined' && location.protocol === 'https:') {
      directives.push('Secure');
    }

    this.document.cookie = directives.join('; ');
  }

  private clearCookie(name: string): void {
    if (!this.document) {
      return;
    }

    const directives = [`${name}=`, 'Path=/', 'Max-Age=0', 'SameSite=Lax'];

    if (typeof location !== 'undefined' && location.protocol === 'https:') {
      directives.push('Secure');
    }

    this.document.cookie = directives.join('; ');
  }
  // get loggedin user
  // This method is used to get user
  getUser() {
    if (localStorage.getItem('login_user')) {
      let user: any = localStorage.getItem('login_user');
      return JSON.parse(user);
    } else {
      return null;
    }
  }
  // This method is used to get current lang
  getCurrentLang(): string {
    let lang: any = localStorage.getItem('selectedLang'); //this.translate.currentLang;
    return lang;
  }
  //Show toast message based on type
  toastMessage(
    title: string,
    message: string,
    type: 'success' | 'error' | 'warning' | 'info',
    showOkButtonOrDirectMessage: boolean | string = false
  ) {
    const showOkButton = typeof showOkButtonOrDirectMessage === 'boolean'
      ? showOkButtonOrDirectMessage
      : false;
    const directMessage = typeof showOkButtonOrDirectMessage === 'string'
      ? showOkButtonOrDirectMessage
      : '';

    const msgkey = `TOAST_MESSAGES.${message}`;
    const titlekey = `TOAST_MESSAGES.${title.toUpperCase()}`;

    const titleTranslation = this.translate.instant(titlekey);
    const messageTranslation = message ? this.translate.instant(msgkey) : '';
    const hasMessageTranslation =
      !!message &&
      !!messageTranslation &&
      messageTranslation !== msgkey;

    // If API returns an explicit message, show it directly without translation.
    let mainMessage = directMessage || (hasMessageTranslation ? messageTranslation : message);
    if (message === 'TECHNICAL_ERROR') {
      const correlationId = this.correlationIdService.getTechnicalErrorCorrelationId();
      if (correlationId) {
        const supportMessage = this.translate.instant(
          'TOAST_MESSAGES.TECHNICAL_ERROR_CORRELATION',
          { correlationId }
        );
        mainMessage = `${mainMessage}<br>${supportMessage}`;
      }
    }

    // If we are showing an OK-required toast, clear previous toasts to avoid stacking
    if (showOkButton) {
      this.toastr.clear();
    }

    // If OK button flag is true, wrap HTML using centralized template
    let okText = this.getCurrentLang() == 'ar' ? 'موافق' : 'Ok';
    let msg = showOkButton
      ? `<div><p class="text-[#000000]!">${mainMessage}</p></div>`
      : mainMessage;

    let toastTitle = (titleTranslation && titleTranslation !== titlekey) ? titleTranslation : title;

    let options = {
      progressBar: !showOkButton,
      positionClass: "toast-top-center",
      enableHtml: true,
      closeButton: false,
      tapToDismiss: showOkButton, //!showOkButton,
      timeOut: showOkButton ? 0 : 3000,
      // toastClass: 'custom-success-toast'
    };

    let toastRef: any;

    if (type === 'success') toastRef = this.toastr.success(msg, toastTitle, options);
    if (type === 'error') toastRef = this.toastr.error(msg, toastTitle, options);
    if (type === 'info') toastRef = this.toastr.info(msg, toastTitle, options);
    if (type === 'warning') toastRef = this.toastr.warning(msg, toastTitle, options);

    // Attach OK button handler only if needed
    if (showOkButton && toastRef?.onShown) {
      toastRef.onShown.subscribe(() => this.attachOkButton(toastRef, okText));
    }
  }

  private attachOkButton(toastRef: any, okText: string): void {
    const toastEl = (toastRef?.toastRef?.el ?? null) as HTMLElement | null;
    const messageEl =
      (toastEl?.querySelector('.toast-message') as HTMLElement | null) ||
      (this.document.querySelector('.toast-message:last-child') as HTMLElement | null);

    if (!messageEl) {
      return;
    }

    let btn = messageEl.querySelector('#toast-ok-btn') as HTMLButtonElement | null;
    if (!btn) {
      btn = this.document.createElement('button');
      btn.id = 'toast-ok-btn';
      btn.type = 'button';
      btn.className =
        'btn-edge-cut toast-ok-btn text-white relative px-4 py-2 font-medium inline-flex items-center justify-center';

      const label = this.document.createElement('span');
      label.className = 'relative z-10';
      label.textContent = okText;
      btn.appendChild(label);

      messageEl.appendChild(btn);
    }

    btn.onclick = () => toastRef.toastRef.close();
  }

  /**
   * Returns products that already have the same HS Code and Country of Origin
   * as the provided product. Use excludeId to skip the current item on edits.
   */
  findProductsWithSameHsAndCountry(
    products: any[] = [],
    targetProduct: any,
    excludeId?: string
  ): any[] {
    const hsCode = (targetProduct?.productHSCode ?? '').toString().trim().toUpperCase();
    const country = (targetProduct?.country ?? '').toString().trim().toUpperCase();
    if (!hsCode || !country) {
      return [];
    }
    return (products || []).filter(p => {
      if (excludeId && p?.id === excludeId) {
        return false;
      }
      const existingHs = (p?.productHSCode ?? '').toString().trim().toUpperCase();
      const existingCountry = (p?.country ?? '').toString().trim().toUpperCase();
      return existingHs === hsCode && existingCountry === country;
    });
  }


  // This method is used to goto detail page using sr
  gotoDetailPage(sr: any) {
    let srServiceType = sr['SR Type'];
    let srServiceSubType = sr['SR Sub Type'] ? sr['SR Sub Type'] : sr['Sub Type'];
    let srNumber = sr['SR Number'];
    const ataCsn = sr['CSN'] ? sr['CSN'] : this.getCsnAccountId('csn');
    const ataSrPathSuffix = ataCsn ? `/${srNumber}/${ataCsn}` : `/${srNumber}`;
    const serviceCsn = sr['CSN'] ? sr['CSN'] : this.getCsnAccountId('csn');
    const serviceSrPathSuffix = serviceCsn ? `/${srNumber}/${serviceCsn}` : `/${srNumber}`;

    if (srServiceType === ServiceName.MEMBERSHIP) {
      if (srServiceSubType == ServiceName.MEMBERSHIP_NEW) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.MEMBERSHIP.REGISTRATION}/${srNumber}`,
        ]);
      }
      if (srServiceSubType == ServiceName.MEMBERSHIP_AMENDMENT) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.MEMBERSHIP.AMENDMENT}/${srNumber}`,
        ]);
      }
      if (srServiceSubType == ServiceName.MEMBERSHIP_RENEW) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.MEMBERSHIP.RENEW}/${srNumber}`,
        ]);
      }
      if (srServiceSubType == ServiceName.MEMBERSHIP_MANAGEMENT_SIGNATORIES) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.MEMBERSHIP.MANAGE_SIGNATORIES}/${srNumber}`,
        ]);
      }
      if (srServiceSubType == ServiceName.MEMBERSHIP_CANCELLATION) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.MEMBERSHIP.CANCELLATION}/${srNumber}`,
        ]);
      }
    }

    if (srServiceSubType == ServiceName.INFORMATION_SERVICES) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.INFORMATION_SERVICE}/${srNumber}`,
      ]);
    }
    if (srServiceSubType == ServiceName.MEDIATION) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.MEDIATION}/${srNumber}`,
      ]);
    }
    if (srServiceSubType == ServiceName.ATTESTATION_AUTH_CERTIFICATE) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.ATTESTATION.AUTHENTICATE}${serviceSrPathSuffix}`,
      ]);
    }
    if (srServiceSubType == ServiceName.ATTESTATION_CERTIFY_DOCUMENT) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.ATTESTATION.CERTIFY}${serviceSrPathSuffix}`,
      ]);
    }
    if (srServiceSubType == ServiceName.ATTESTATION_VERIFY_SIGNATURE) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.ATTESTATION.VERIFY_SIGNATURE}${serviceSrPathSuffix}`,
      ]);
    }
    if (srServiceType == ServiceName.ATA_CARNET) {
      if (srServiceSubType == ServiceName.ISSUE_NEW_CARNET) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.ATA.CARNET}${ataSrPathSuffix}`,
        ]);
      }
      if (srServiceSubType == ServiceName.AMENDMENT_CARNET) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.ATA.AMENDMENT}${ataSrPathSuffix}`,
        ]);
      }
      if (srServiceSubType == ServiceName.REPLACE_CARNET) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.ATA.REPLACE}${ataSrPathSuffix}`,
        ]);
      }
      if (srServiceSubType == ServiceName.SUBSTITUTE_CARNET) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.ATA.SUBSTITUTE}${ataSrPathSuffix}`,
        ]);
      }
      if (srServiceSubType == ServiceName.REFUND_CARNET) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.ATA.REFUND}${ataSrPathSuffix}`,
        ]);
      }
      if (srServiceSubType == ServiceName.NOC_LETTER_CARNET) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.ATA.NOC_LETTER}${ataSrPathSuffix}`,
        ]);
      }
    }
    if (srServiceType == ServiceName.COO) {
      if (srServiceSubType == ServiceName.ISSUE_NEW_COO) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.COO.ISSUE}${serviceSrPathSuffix}`,
        ]);
      }
      if (srServiceSubType == ServiceName.AMENDMENT_COO) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.COO.AMENDMENT}${serviceSrPathSuffix}`,
        ]);
      }
      if (srServiceSubType == ServiceName.PERSONAL_EFFECTS_COO) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.COO.PERSONAL}${serviceSrPathSuffix}`,
        ]);
      }
      if (srServiceSubType == ServiceName.ISSUE_GSP_COO) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.COO.GSP_CERTIFICATE}${serviceSrPathSuffix}`,
        ]);
      }
    }
    if (srServiceSubType == ServiceName.USER_ACCESS) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.ADD_MORE_BUSINESS}/${srNumber}`,
      ]);
    }
    if (srServiceSubType == ServiceName.PAYMENT_ADVANCE_FOR_SERVICES) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.PAYMENT}/${srNumber}`,
      ]);
    }
    if (srServiceSubType == ServiceName.MYSTERY_SHOPPER_MEMBERSHIP) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.MYSTERY_SHOPPER}/${srNumber}`,
      ]);
    }
    if (srServiceSubType == ServiceName.MRM_APPLICATION) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.MRM_AWARDS}/${srNumber}`,
      ]);
    }
    if (srServiceSubType == ServiceName.MRM_TRAINING_WORKSHOP) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.MRM_TRAINING_WORKSHOP}/${srNumber}`,
      ]);
    }
    if (srServiceSubType == ServiceName.CHAMBER_ENTREPRENEURS) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.CHAMBER_ENTREPRENEURS}/${srNumber}`,
      ]);
    }
    if (srServiceSubType == ServiceName.VENUE_BOOKING) {
      this.router.navigate([
        `${RoutesPath.SERVICE}/${RoutesPath.VENUE.BOOKING_DETAIL}/${serviceSrPathSuffix}`,
      ]);
    }
    if (srServiceType == ServiceName.BUSINESS_GROUP || srServiceType == ServiceName.BUSINESS_COUNCIL) {
      if (sr['Status'] == SrStatus.Draft || sr['Status'] == SrStatus.Open || sr['Status'] == SrStatus.AwaitingClarification) {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.BUSINESS_GROUP}/${RoutesPath.ESTABLISH_BGC_FORM}/${srNumber}`,
        ]);
      } else {
        this.router.navigate([
          `${RoutesPath.SERVICE}/${RoutesPath.BUSINESS_GROUP}/${RoutesPath.BGC_APPLICATION_VIEW}/${srNumber}`,
        ]);
      }
    }
  }

  // This method is used to set sr deails using sr detail
  setSrResponse(srDetail: any) {
    sessionStorage.setItem('srDetail', JSON.stringify(srDetail));
  }
  // This method is used to get sr deails
  getSrResponse() {
    let sr: any = '';
    if (sessionStorage.getItem('srDetail') != 'undefined') {
      let store: any = sessionStorage.getItem('srDetail');
      sr = JSON.parse(store);
    }
    return sr;
  }



  // Draft            `dc-status-brand`
  // Action Required  `dc-status-warning`
  // In Progress      `dc-status-info`
  // Completed        `dc-status-success`
  // Rejected         `dc-status-negative`

  // This method is used to get status class using status
  getStatusClass(status: string): string {
    if (!status) return 'dc-status-default';
    // Brand (Draft / Inactive)
    if (
      status === SrStatus.Draft ||
      status === SrStatus.Open ||
      status === SrStatus.DraftingInProgress
    ) {
      return 'dc-status-brand';
    }
    // Information (No user action required)
    if (
      status === SrStatus.SubmittedForApproval ||
      status === SrStatus.ReviewInProgress ||
      status === SrStatus.ReviewedWithComments ||
      status === SrStatus.AssignedToCS ||
      status === SrStatus.AwaitingOriginalCarnet ||
      status === SrStatus.AwaitingDischarge ||
      status === SrStatus.AwaitingCarnetReturn ||
      status === SrStatus.PendingApproval
    ) {
      return 'dc-status-info';
    }

    // Warning / Action Required
    if (
      status === SrStatus.InProgress ||
      status === SrStatus.AmendmentInProgress ||
      status === SrStatus.ApprovedAndPaymentAwaited ||
      status === SrStatus.AwaitingClarification ||
      status === SrStatus.AwaitingFinalPayment
    ) {
      return 'dc-status-warning';
    }

    // Positive Closed
    if (
      status === SrStatus.Approved ||
      status === SrStatus.Paid ||
      status === SrStatus.PaymentReceived ||
      status === SrStatus.ExcessPaid ||
      status === SrStatus.OriginalCarnetReceived ||
      status === SrStatus.VenueBooked ||
      status === SrStatus.Booked ||
      status === SrStatus.Closed ||
      status === SrStatus.Obsolete ||
      status === "Closed"
    ) {
      return 'dc-status-success';
    }
    // Negative Closed
    if (
      status === SrStatus.Rejected ||
      status === SrStatus.Cancelled
    ) {
      return 'dc-status-negative';
    }

    return 'dc-status-info'; //'dc-status-default';
  }


  // This method is used to get encryption using service type and options
  getEncryption(
    serviceType: string,
    options?: {
      srNumber?: string,
      subType?: string,
      cooMot?: string,
      licenseType?: string,
      svsSubject?: string,
      caseNo?: string,
      licCatg?: string,
      memNumber?: string,
      userType?: string
    }
  ): Observable<any> {
    const requestPayload: any = {
      "body": {
        "ProcessName": "DC Jupiter Encryption WF",
        "DC Channel": this.getCurrentLang() == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
        "DC Method": `${DcMethodValues.SHARED_SERVICE} - ${serviceType}`,
        "serviceType": serviceType,
        "memNumber": options?.memNumber || "",
        "srNumber": options?.srNumber || "",
        "caseNo": options?.caseNo || "",
        "svsSubject": options?.svsSubject || "",
        "mot": options?.cooMot || "",
        "licCatg": options?.licCatg || "",
        "subType": options?.subType || "",
        "userType": options?.userType || "Customer"
      }
    };
    return this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.RUN_PROCESS,
      requestPayload
    );

  }

  // This method is used to get documents using sr number, document service type, is labels, and options
  getDocuments(
    srNumber: string,
    documentServiceType: string,
    isLabels?: boolean,
    options?: {
      subType?: string;
      cooMot?: string;
      licenseType?: string;
      svsSubject?: string;
      memNumber?: string;
      userType?: string;
      caseNo?: string;
    }
  ): Observable<any> {
    const dubaiTime = Utils.getDubaiDateTime();

    // Step 1: Call encryption
    return this.getEncryption(documentServiceType, {
      srNumber: srNumber,
      subType: options?.subType,
      cooMot: options?.cooMot,
      svsSubject: options?.svsSubject,
      licCatg: options?.licenseType,
      memNumber: options?.memNumber,
      userType: options?.userType ?? 'Customer',
      caseNo: options?.caseNo
    }).pipe(
      switchMap((encryptionResponse: EncryptionResponse) => {
        if (!encryptionResponse) {
          return throwError(() => 'ENCRYPTION_FAILED');
        }

        // Step 2: Decode response and stringify
        const response: any = Utils.decodeAndStringify(encryptionResponse);
        const decodedJson: string = JSON.stringify(response, null, 2);

        // Step 3: Build requestPayload with encryption
        let requestPayload: any;

        if (environment.documentEncryption && response) {
          requestPayload = {
            serviceType: response.serviceType,
            srNumber: response.srNumber ?? srNumber ?? '',
            memNumber: response.memNumber ?? '',
            validation: response.validation,
            licCatg: response.licCatg ?? options?.licenseType ?? '',
            subType: response.subType ?? '',
            userType: response?.userType ?? 'Customer',
            caseNo: response?.caseNo ?? ''
          };
          if (
            documentServiceType === DocumentServiceType.ATTESTATION &&
            options?.svsSubject
          ) {
            requestPayload['svsSubject'] = options.svsSubject == "Other" ? response.svsSubject : options.svsSubject; //?? options.svsSubject;
          }
          if (documentServiceType === DocumentServiceType.COO && options?.cooMot) {
            requestPayload['mot'] = response.mot ?? options.cooMot;
          }
        } else {
          requestPayload = {
            serviceType: documentServiceType,
            srNumber: srNumber ?? '',
            validation: dubaiTime,
            licCatg: options?.licenseType ?? '',
            subType: options?.subType ?? '',
            userType: options?.userType ?? 'Customer',
            caseNo: options?.caseNo ?? ''
          };
          if (
            documentServiceType === DocumentServiceType.ATTESTATION &&
            options?.svsSubject
          ) {
            requestPayload['svsSubject'] = options.svsSubject;
          }
          if (documentServiceType === DocumentServiceType.COO && options?.cooMot) {
            requestPayload['mot'] = options.cooMot;
          }
        }

        // Step 4: Call actual API
        if (isLabels ?? true) {
          return this.attachmentService.getRequest(
            ApiRoot.JUPITER_REST,
            ApiEndPoints.DOCUMENT_GET_TYPES,
            requestPayload
          );
        } else {
          return this.api.post(
            ApiRoot.JUPITER_REST,
            ApiEndPoints.DOCUMENT_OPEN_FOLDER,
            requestPayload
          );
        }
      })
    );
  }
  /** Get only docs for a specific section (e.g. null → main form section) */
  // This method is used to filter docs by section using folder and section
  filterDocsBySection(
    folder: FolderResponse,
    section: string | null
  ): AllowedDocumentType[] {
    return (
      folder.allowedDocumentTypes?.filter((d) => d.forUISection === section) ??
      []
    );
  }

  /** Validate required documents */
  // This method is used to validate required documents using folder and partner type
  validateRequiredDocuments(
    folder: FolderResponse,
    partnerType?: 'COMPANY' | 'PERSON' | null
  ): DocumentValidationResult {
    const missingFiles: Record<string, boolean> = {};
    const missingDocs: string[] = [];

    folder?.allowedDocumentTypes?.forEach((docType) => {
      // check if doc belongs to current section
      const belongsToSection =
        !docType.forUISection || // always include null (common docs)
        docType.forUISection === partnerType;

      if (belongsToSection && docType.mandatory) {
        const uploaded =
          folder.documents?.some(
            (doc) => doc.documentType === docType.nameEn
          ) ?? false;

        missingFiles[docType.nameEn] = !uploaded;

        if (!uploaded) {
          missingDocs.push(docType.nameEn);
        }
      } else {
        // Not mandatory or not for this section
        missingFiles[docType.nameEn] = false;
      }
    });

    return {
      missingFiles,
      missingDocs,
      isValid: missingDocs.length === 0,
    };
  }

  // This method is used to get dc all members using search text and search by
  getDCAllMembers(searchText: string = '', searchBy: string = 'MemberName') {
    // Regex for Arabic characters (basic Arabic + extended)
    const isArabic = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/.test(
      searchText
    );
    const requestPayload: any = {
      body: {
        ProcessName: 'DC Get Active Member Details WF',
        DCNameArabic: searchBy == 'MemberName' && isArabic ? searchText : '',
        DCNameEnglish:
          searchBy == 'MemberName' && isArabic
            ? ''
            : searchBy == 'MemberName'
              ? searchText
              : '',
        AccountTypeCode: 'Member',
      },
    };
    if (searchBy == 'LicenseNumber') {
      requestPayload.body.LicNum = searchText; //68552
    }
    if (searchBy == 'CSN') {
      requestPayload.body.CSN = searchText; //338773
    }
    return this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_DC_ALL_MEMBERS,
      requestPayload
    );
  }

  requestPayload: ApplicationRequest = new ApplicationRequest();
  // This method is used to get pending application using account csn, service type, sub service type, status, page size, and no days
  getPendingApplication(
    accountCSN: string,
    serviceType: string,
    subServiceType: string,
    status: string,
    pageSize: any = 1,
    noDays: any = 30
  ): Observable<any> {
    this.requestPayload.body.IncomingHierarchy.MessageId =
      Utils.generateUniqueId();

    this.requestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO[
      'Service Request'
    ]['Account Id'] = '';
    this.requestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO[
      'Service Request'
    ].CSN = accountCSN; //accountCSN;
    this.requestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO[
      'Service Request'
    ]['SR Number'] = '';
    this.requestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO[
      'Service Request'
    ]['SR Type'] = serviceType || '';
    this.requestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO[
      'Service Request'
    ]['SR Sub Type'] = subServiceType || '';
    this.requestPayload.body.IncomingHierarchy.ListOfDCGetSRSummaryIO[
      'Service Request'
    ]['Status'] = status;

    this.requestPayload.body.PageSize = pageSize;
    this.requestPayload.body.StartRowNum = 0;
    this.requestPayload.body.NewQuery = true;
    this.requestPayload.body.NoDays = noDays;

    return this.api.post(
      ApiRoot.DCCICPIntegration,
      ApiEndPoints.GET_SR_SUMMARY,
      this.requestPayload
    );
  }


  // This method is used to check inprogress application using account csn, service type, and sub service type
  checkInprogressApplication(
    accountCSN: string,
    serviceType: string,
    subServiceType: string,
  ): Observable<any> {
    const requestPayload = new PendingApplicationRequest();
    requestPayload.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();
    requestPayload.body.DCLanguage = this.getCurrentLang() == 'ar' ? 'ARA' : 'ENU';
    requestPayload.body.DCLoginName = this.getUserProfile()['Login Name'];
    requestPayload.body.DCIncomingHierarchy.MessageId = Utils.generateUniqueId();

    requestPayload.body.DCIncomingHierarchy['ListOfDC Community Portal SR IO']['DC Community Portal SR']['SR Type'] = serviceType;
    requestPayload.body.DCIncomingHierarchy['ListOfDC Community Portal SR IO']['DC Community Portal SR']['Sub Type'] = subServiceType;
    requestPayload.body.DCIncomingHierarchy['ListOfDC Community Portal SR IO']['DC Community Portal SR'].CSN = accountCSN;

    return this.api.post(
      ApiRoot.SERVICE_REQUEST_INTEGRATION_API_REST,
      ApiEndPoints.CHECK_OPEN_SR,
      requestPayload
    );
  }

  // This method is used to delete bulk document using data
  deleteBulkDocument(data: any): any {
    this.getEncryption(data.documentServiceType, { cooMot: data.mot, svsSubject: data.svsSubject })
      .subscribe({
        // This method is used to handle the next callback using encryption response
        next: (encryptionResponse: EncryptionResponse) => {
          if (!encryptionResponse.validation) {
            return;
          }
          // Step 2: Decode response and stringify
          const response: any = Utils.decodeAndStringify(encryptionResponse);
          const decodedJson: string = JSON.stringify(response, null, 2);

          this.attachmentService.deleteBulkDocuments(ApiRoot.JUPITER_REST, ApiEndPoints.DELETE_BULK_DOCUMENT, {
            documentIds: data.documentId,
            folderId: data.folderId,
            folderParentId: data.folderParentId,
            folderParentType: data.folderParentType,
            validation: environment.documentEncryption ? response.validation : ''
          }).subscribe({
            // This method is used to handle the next callback using response
            next: (response: any) => {
              if (response) {
              }
            },
            // This method is used to handle the error callback using e
            error: (e) => {
              // this.toastMessage('ERROR', '', 'error', e?.error?.error?.details[0]);
            }
          });
        },
        // This method is used to handle the error callback
        error: () => {
          return;
        }
      })
  }


  // This method is used to get valid carnet list using result
  getValidCarnetList_old(result: any[]): any[] {
    return result
      ?.filter((c: any) => {
        const carnetNumber = c['DC Carnet Number'];
        return carnetNumber !== null && carnetNumber !== undefined && carnetNumber.toString().trim() !== '' && c['DC ATA Carnet Validity Dt'] != null;
      })
      .map((c: any) => {
        const label = `${c['DC Carnet Number']} (${c['SR Number']})`;
        return {
          ...c,
          Name: label,
          Value: c['DC Carnet Number'],
        };
      }) ?? [];
  }

  getValidCarnetList(result: any[]): any[] {
    if (!Array.isArray(result)) return [];
    // Step 1: Filter valid carnet records
    const validCarnets = result.filter((c: any) => {
      const carnetNumber = c['DC Carnet Number'];
      return (
        carnetNumber &&
        carnetNumber.toString().trim() !== '' &&
        c['DC ATA Carnet Validity Dt']
      );
    });

    // Step 2: Group by Carnet Number and pick the latest by "Created"
    const latestByCarnet: Record<string, any> = {};

    validCarnets.forEach((c: any) => {
      const carnetNumber = c['DC Carnet Number'];
      const createdDate = new Date(c['Created']);

      if (
        !latestByCarnet[carnetNumber] ||
        createdDate > new Date(latestByCarnet[carnetNumber]['Created'])
      ) {
        latestByCarnet[carnetNumber] = c;
      }
    });

    // Step 3: Map to final format
    return Object.values(latestByCarnet).map((c: any) => ({
      ...c,
      Name: `${c['DC Carnet Number']} (${c['SR Number']})`,
      Value: c['DC Carnet Number'],
    }));
  }



  // This method is used to get ATA service consent detail using data
  getATAConsentDetail(data: any): any {
    const isArabic = data.language === 'ar';
    // This method is used to wrap letter using heading and paragraphs
    const wrapLetter = (heading: string, paragraphs: string[]): string => `
          <div class="space-y-4 text-sm leading-6 text-dc-navy">
            <p class="text-base font-semibold underline">${heading}</p>
            ${paragraphs.join('\n            ')}
          </div>`;

    let dcLetter = '';

    switch (data.SRSubType) {
      case ServiceName.SUBSTITUTE_CARNET: {
        if (isArabic) {
          const paragraphs = [
            `<p>نحن, <b>${data.companyName}</b>, على علم بإصدار دفتر الإدخال المؤقت بدل فاقد لمصلحتنا وذلك <b>${data.reasonOrPurpose}</b>.</p>`,
            `<p>كما إننا نتعهد بتحمل أية مطالبات أو تعويضات مالية وإبقاء غرفة تجارة وصناعة دبي وموظفيها سليمة ومبرأة من أية مسؤولية تنتج عن إجراءات التصدير المؤقت للبضائع عبر دفتر الإدخال المؤقت.</p>`,
            `<p>كما نتعهد بالإضافة إلى ذلك تقديم أية مستندات ثبوتية عند الطلب مستقبلا، كما نلتزم بقوانين ولوائح ونظم دفتر الإدخال المؤقت طبقا لاتفاقية إسطنبول .</p>`,
            `<p class="font-semibold">ملاحظة: قد لايتم قبول دفتر الادخال المؤقت "بدل فاقد" من قبل بعض الدول الاعضاء في سلسلة نظام دفاتر الادخال المؤقت وفي هذه الحالة قد يحتاج مقدم الطلب الحصول على موافقة مسبقة من قبل جمارك الدولة المستوردة. </p>`
          ];
          dcLetter = wrapLetter('طلب دفتر إدخال مؤقت – بدل فاقد', paragraphs);
        } else {
          const paragraphs = [
            `<p class="font-medium">Dear Sir,</p>`,
            `<p>We the undersigned, <b>${data.companyName}</b>, confirm our awareness of your issuing Substitute ATA carnet in our favour for <b>${data.reasonOrPurpose}</b>.</p>`,
            `<p>We undertake, for so long as each ATA carnet issue on our favour remains valid, to ensure that our company fulfils its obligations in the respect of the rules applicable to the ATA carnets and further declare that we shall keep you safe and harmless against any liabilities or charges that may be attributable to our fault or negligence in connection with the issuance of such ATA Carnets in our favour.</p>`,
            `<p class="font-semibold">Note: Substitute Carnets might not be accepted by certain countries member in the ATA Carnet chain and the requester need to get the approval from the customs in the importing country. </p>`
          ];
          dcLetter = wrapLetter('Subject:  Substitute Carnet request letter', paragraphs);
        }
        break;
      }

      case ServiceName.REPLACE_CARNET: {
        if (isArabic) {
          const paragraphs = [
            `<p>نحن  <b>${data.companyName},</b> على علم بإصدار دفتر الإدخال المؤقت البديل لمصلحتنا وذلك  <b>${data.reasonOrPurpose}.</b></p>`,
            `<p>كما إننا نتعهد بتحمل أية مطالبات أو تعويضات مالية وإبقاء غرفة تجارة وصناعة دبي وموظفيها سليمة ومبرأة من أية مسؤولية تنتج عن إجراءات التصدير المؤقت للبضائع عبر دفتر الإدخال المؤقت.</p>`,
            `<p>كما نتعهد بالإضافة إلى ذلك تقديم أية مستندات ثبوتية عند الطلب مستقبلا، كما نلتزم بقوانين ولوائح ونظم دفتر الإدخال المؤقت طبقا لاتفاقية إسطنبول .</p>`,
            `<p class="font-semibold">ملاحظة: قد لايتم قبول دفتر الادخال المؤقت "بدل فاقد" من قبل بعض الدول الاعضاء في سلسلة نظام دفاتر الادخال المؤقت وفي هذه الحالة قد يحتاج مقدم الطلب الحصول على موافقة مسبقة من قبل جمارك الدولة المستوردة. </p>`
          ];
          dcLetter = wrapLetter('طلب دفتر إدخال مؤقت - بديل', paragraphs);
        } else {
          const paragraphs = [
            `<p class="font-medium">Dear Sir,</p>`,
            `<p>We the undersigned, <b>${data.companyName},</b> confirm our awareness of your issuing Replacement ATA carnet in our favour for <b>${data.reasonOrPurpose}.</b></p>`,
            `<p>We undertake, for so long as each ATA carnet issue on our favour remains valid, to ensure that our company fulfils its obligations in the respect of the rules applicable to the ATA carnets and further declare that we shall keep you safe and harmless against any liabilities or charges that may be attributable to our fault or negligence in connection with the issuance of such ATA Carnets in our favour.</p>`,
            `<p class="font-semibold">Note: Replacement Carnet might not be accepted by certain countries member in the ATA Carnet chain and the requester need to get the approval from the customs in the importing country. </p>`
          ];
          dcLetter = wrapLetter('Subject:  Replacement Carnet request letter', paragraphs);
        }
        break;
      }

      case ServiceName.ISSUE_NEW_CARNET:
      case ServiceName.AMENDMENT_CARNET: {
        if (isArabic) {
          const paragraphs = [
            `<p>نحن  <b>${data.companyName},</b> على علم بإصدار دفتر الإدخال المؤقت البديل لمصلحتنا وذلك  <b>${data.reasonOrPurpose}.</b></p>`,
            `<p>كما إننا نتعهد بتحمل أية مطالبات أو تعويضات مالية وإبقاء غرفة تجارة وصناعة دبي وموظفيها سليمة ومبرأة من أية مسؤولية تنتج عن إجراءات التصدير المؤقت للبضائع عبر دفتر الإدخال المؤقت.</p>`,
            `<p>كما نتعهد بالإضافة إلى ذلك تقديم أية مستندات ثبوتية عند الطلب مستقبلا، كما نلتزم بقوانين ولوائح ونظم دفتر الإدخال المؤقت طبقا لاتفاقية إسطنبول .</p>`,
            `<p class="font-semibold">ملاحظة: قد لايتم قبول دفتر الادخال المؤقت "بدل فاقد" من قبل بعض الدول الاعضاء في سلسلة نظام دفاتر الادخال المؤقت وفي هذه الحالة قد يحتاج مقدم الطلب الحصول على موافقة مسبقة من قبل جمارك الدولة المستوردة. </p>`
          ];
          dcLetter = wrapLetter('طلب دفتر إدخال مؤقت - بديل', paragraphs);
        } else {
          const paragraphs = [
            `<p class="font-medium">Dear Sir,</p>`,
            `<p>We the undersigned, <b>${data.companyName},</b> acknowledge the issuance of ATA carnets on our behalf for the temporary admission of goods.</p>`,
            `<p>We undertake, for the duration of each ATA Carnet’s validity, to ensure our company complies with all applicable ATA Carnet regulations.</p>`,
            `<p>Furthermore, we agree to indemnify and hold you harmless from any liabilities, claims, or costs arising from our fault or negligence in connection with these ATA Carnets.</p>`
          ];
          dcLetter = wrapLetter('Subject:  Pledged Commitment letter', paragraphs);
        }
        break;
      }

      case 'Cancellation of ATA Carnet': {
        const srReference = data.srNumber ? `#${data.srNumber}` : '';
        if (isArabic) {
          const paragraphs = [
            `<p>نرجوا من سيادتكم الغاء طلب دفتر الادخال المؤقت الذي تم تقديمه <b>${data.companyName}</b> تحت رقم الطلب  <b>${srReference}</b> وذلك بسبب  <b>${data.reasonOrPurpose}</b>, نود أن نطلب النظر في قبول طلبنا وبدء عملية استرداد الرسوم..</p>`,
            `<p>كما إننا نتعهد بتحمل أية مطالبات أو تعويضات مالية وإبقاء غرفة تجارة وصناعة دبي وموظفيها سليمة ومبرأة من أية مسؤولية تنتج عن إجراءات التصدير المؤقت للبضائع عبر دفتر الإدخال المؤقت.</p>`,
            `<p>كما نتعهد بالإضافة إلى ذلك تقديم أية مستندات ثبوتية عند الطلب مستقبلا، كما نلتزم بقوانين ولوائح ونظم دفتر الإدخال المؤقت طبقا لاتفاقية إسطنبول .</p>`,
            `<p>شاكرين لكم قبول طلبنا وارجاع مبلغ المستحق والمدفوع مسبقا.</p>`
          ];
          dcLetter = wrapLetter('طلب إلغاء دفتر إدخال مؤقت ', paragraphs);
        } else {
          const paragraphs = [
            `<p>Kindly Cancel our ATA request which was submitted under our company <b>${data.companyName}</b> under service request <b>${srReference}</b> and the Reason for cancellation is due to <b>${data.reasonOrPurpose}</b>, we would like to request to consider the acceptance of our request and initiate the process of fees refund.</p>`,
            `<p>Our company will fulfil its obligations in the respect of the rules applicable to the ATA carnets and further declare that we shall keep you safe and harmless against any liabilities or charges that may be attributable to our fault or negligence in connection with the issuance of such ATA Carnets in our favour.</p>`,
            `<p>Admin fees refund are subject for management approval.</p>`
          ];
          dcLetter = wrapLetter('Subject:  Carnet Cancellation request letter', paragraphs);
        }
        break;
      }

      case ServiceName.REFUND_CARNET: {
        if (isArabic) {
          const paragraphs = [
            `<p>نحن  <b>${data.companyName},</b> على علم بإصدار دفتر الإدخال المؤقت البديل لمصلحتنا وذلك  <b>${data.reasonOrPurpose}.</b></p>`,
            `<p>كما إننا نتعهد بتحمل أية مطالبات أو تعويضات مالية وإبقاء غرفة تجارة وصناعة دبي وموظفيها سليمة ومبرأة من أية مسؤولية تنتج عن إجراءات التصدير المؤقت للبضائع عبر دفتر الإدخال المؤقت.</p>`,
            `<p>كما نتعهد بالإضافة إلى ذلك تقديم أية مستندات ثبوتية عند الطلب مستقبلا، كما نلتزم بقوانين ولوائح ونظم دفتر الإدخال المؤقت طبقا لاتفاقية إسطنبول .</p>`,
            `<p class="font-semibold">ملاحظة: قد لايتم قبول دفتر الادخال المؤقت "بدل فاقد" من قبل بعض الدول الاعضاء في سلسلة نظام دفاتر الادخال المؤقت وفي هذه الحالة قد يحتاج مقدم الطلب الحصول على موافقة مسبقة من قبل جمارك الدولة المستوردة. </p>`
          ];
          dcLetter = wrapLetter('طلب دفتر إدخال مؤقت - بديل', paragraphs);
        } else {
          const paragraphs = [
            `<p class="font-medium">Dear Sir,</p>`,
            `<p>We the undersigned, <b>${data.companyName},</b> hereby confirm that the bank details provided in the relevant form are accurate and true to the best of our knowledge.</p>`,
            `<p>We acknowledge that the Dubai Chamber bears no responsibility for any errors, discrepancies, or consequences arising from the information submitted by us.</p>`
          ];
          dcLetter = wrapLetter('Subject: Bank Details Confirmation Consent', paragraphs);
        }
        break;
      }
    }
    return dcLetter;
  }

  // This method is used to return owner/partner docuemtns
  getOwnerPartnerDocuments(srFolder: any, partnerType: string) {
    if (!srFolder?.allowedDocumentTypes) {
      return [];
    }
    // Filter documents based on partner type
    const docs = srFolder?.allowedDocumentTypes.filter((d: any) =>
      partnerType === FieldValue.COMPANY
        ? d.forUISection === 'COMPANY'
        : d.forUISection === 'PERSON'
    );

    docs.forEach((doc: any) => {
      const docTypeKey = (doc.documentType || doc.nameEn || '').toString().toLowerCase();
      if (docTypeKey === 'signature_card' || docTypeKey === 'passport_or_emirates_id_copy') {
        doc.mandatory = false;
      }
    });
    return docs;
  }

  hasDocumentUploaded(srFolder: any, notesValue: string, targetType: string): boolean {
    const expectedType = (targetType || '').toLowerCase();
    const currentNotes = (notesValue || '').toString().toLowerCase();
    const documents = srFolder?.documents || [];

    return documents.some((uploaded: any) => {
      const docType = (uploaded?.documentType || uploaded?.nameEn || '').toString().toLowerCase();
      if (docType !== expectedType) {
        return false;
      }
      const rawNotes = (uploaded?.notes ?? '').toString().toLowerCase();
      if (!currentNotes) {
        return rawNotes === '';
      }
      return rawNotes === currentNotes;
    });
  }
  // This method is used to set primary account activities
  setPrimaryMemberActivities(activities: any) {
    localStorage.setItem('primaryAccountActivities', JSON.stringify(activities));
  }

  // This method is used to get primary account activities
  getPrimaryMemberActivities() {
    let activities: any = '';
    if (localStorage.getItem('primaryAccountActivities') != 'undefined') {
      let store: any = localStorage.getItem('primaryAccountActivities');
      activities = JSON.parse(store);
    }
    return activities;
  }

  //Download Service Manual
  downloadServiceManual() {
    let url = "https://www.dubaichambers.com/documents/20119/1416528/Service_Manual_En.pdf/898401db-71e9-6523-f3c4-7c32732068da?t=1736843779637";
    if (this.getCurrentLang() == 'ar') {
      url = "https://www.dubaichambers.com/documents/20119/1416528/Service_Manual_Ar.pdf/3c2651af-b1ca-fc04-e2eb-ab0f5df773c1?t=1736913758941"
    }
    window.open(url, '_blank', 'noopener');
  }

  checkPrimaryUser(csn: string): boolean {
    const userDetails: any = this.getUserProfile();
    const accounts = Utils.checkIsArray(userDetails?.Account) ?? [];

    const member = accounts?.find((m: any) => m.CSN === csn);

    return !!(
      member &&
      member['Primary User'] === 'Y'
    );
    //return (this.getPrimaryAccount()?.['Primary User'] == "Y" && this.getCsnAccountId('csn') == csn);
  }

  getAccountWithDceStartDate(): any {
    const userDetails: any = this.getUserProfile();
    const accounts = Utils.checkIsArray(userDetails?.Account) ?? [];

    return accounts.find(
      (m: any) => m['DC DCE Start Date'] != null && m['DC DCE Start Date'] !== ''
    );
  }


  private hasValue(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    return String(value).trim() !== '';
  }

  private isYesNoFlag(value: any): boolean {
    if (value === null || value === undefined) {
      return false;
    }
    const normalized = String(value).trim().toUpperCase();
    return normalized === 'Y' || normalized === 'N';
  }

  private isCompanyInformationComplete(profile: any): boolean {
    const hasCompanyName = this.hasValue(profile?.['DC Legal Company Name']);
    const hasIndustry = this.hasValue(profile?.['DC Sector/Industry']);
    const hasEmployees = this.hasValue(profile?.['DC Number of Employees']);
    const hasCommunication = this.hasValue(
      profile?.['DC Preferred Communication Channel']
    );
    const hasIndustrySurveyFlag = this.isYesNoFlag(
      profile?.['DC Industry servey Flag']
    );
    const hasPromotionFlag = this.isYesNoFlag(
      profile?.['DC Pramotion update  Flag']
    );

    const prioritiesRaw = profile?.['DC Company Business Priorities BC'];
    const priorities = Utils.checkIsArray(prioritiesRaw);
    const hasPriorities = priorities.some((priority: any) =>
      this.hasValue(priority?.['DC Business Priorities'])
    );

    return (
      hasCompanyName &&
      hasIndustry &&
      hasEmployees &&
      hasCommunication &&
      hasIndustrySurveyFlag &&
      hasPromotionFlag &&
      hasPriorities
    );
  }

  checkCompanyInformationRequiredFields(accountId: string): Observable<boolean> {
    const requestPayload = {
      body: {
        ProcessName: 'DC Create Company Profile WF',
        DCMethod: 'GETPROFILE',
        AccountId: accountId ?? '',
        "DC Channel": this.getCurrentLang() == 'ar' ? FieldValue.CHANEL_ONLINE_AR : FieldValue.CHANEL_ONLINE_EN,
      },
    };
    return this.api
      .post(
        ApiRoot.DCCICPIntegration,
        ApiEndPoints.CREATE_UPDATE_SR,
        requestPayload
      )
      .pipe(
        map((response: any) => {
          const profile =
            response?.SiebelMessage?.ListOfDCCompanyProfileIO?.[
            'DC Company Profile BC'
            ] || response?.SiebelMessage?.['DC Company Profile BC'];
          if (!profile) {
            return false;
          }
          return this.isCompanyInformationComplete(profile);
        })
      );
  }

}



